import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleIcon from "@mui/icons-material/People";
import SyncIcon from "@mui/icons-material/Sync";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useNavigate, useLocation } from "react-router-dom";
import {
  logoutAsync,
  selectIsAuthenticated,
  selectUserProfile,
} from "../../features/authentication/authenticationSlice";
import {
  selectProjects,
  selectSelectedProject,
  selectSelectedMilestone,
  setSelectedProject,
  setSelectedMilestone,
} from "../../features/projects/projectSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ROUTES } from "../../routes/routes";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const user = useAppSelector(selectUserProfile);
  const dispatch = useAppDispatch();
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await dispatch(logoutAsync());
  };

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const projects = useAppSelector(selectProjects);
  const selectedProject = useAppSelector(selectSelectedProject);
  const selectedMilestone = useAppSelector(selectSelectedMilestone);

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    const project = projects.find((p) => p.name === event.target.value);
    if (project) {
      dispatch(setSelectedProject(project));
      const firstMilestone = project.milestones.find((m) => m.name !== "Unassigned") || null;
      if (firstMilestone) {
        dispatch(setSelectedMilestone(firstMilestone));
        if (location.pathname === "/" || location.pathname.startsWith("/project/")) {
          navigate(`/project/${project.id}/milestone/${firstMilestone.id}`);
        }
      }
    }
  };

  return (
    <AppBar
      position="static"
      sx={{
        background: "linear-gradient(135deg, #0a6baa 0%, #0e86d4 50%, #1a9be6 100%)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderLeft: "none",
        borderRight: "none",
      }}
    >
      <Toolbar
        sx={{
          maxWidth: { xl: "1536px" },
          mx: "auto",
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          onClick={() => navigate("/")}
          sx={{
            cursor: "pointer",
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M32 320 L144 320 L192 384 L280 128 L328 384 L380 320 L480 320" stroke="#ffffff" strokeWidth="28" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <line x1="280" y1="36" x2="280" y2="116" stroke="#ffffff" strokeWidth="12" strokeLinecap="round"/>
            <line x1="240" y1="76" x2="320" y2="76" stroke="#ffffff" strokeWidth="12" strokeLinecap="round"/>
            <line x1="248" y1="44" x2="312" y2="108" stroke="#ffffff" strokeWidth="12" strokeLinecap="round"/>
            <line x1="312" y1="44" x2="248" y2="108" stroke="#ffffff" strokeWidth="12" strokeLinecap="round"/>
          </svg>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.02em",
              fontSize: "1.25rem",
            }}
          >
            TeamRay
          </Typography>
        </Box>
        {isAuthenticated && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 2 }}>
            <Button
              startIcon={<AssessmentIcon />}
              onClick={() => {
                if (selectedProject && selectedProject.id > 0 && selectedMilestone && selectedMilestone.id > 0) {
                  navigate(`/project/${selectedProject.id}/milestone/${selectedMilestone.id}`);
                } else {
                  navigate("/");
                }
              }}
              sx={{
                color: "white",
                textTransform: "none",
                fontWeight: location.pathname === "/" || location.pathname.startsWith("/project/") ? 700 : 400,
                opacity: location.pathname === "/" || location.pathname.startsWith("/project/") ? 1 : 0.75,
                borderBottom: location.pathname === "/" || location.pathname.startsWith("/project/") ? "2px solid white" : "2px solid transparent",
                borderRadius: 0,
                px: 1.5,
                "&:hover": { opacity: 1, backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              Milestones
            </Button>
            <Button
              startIcon={<AccessTimeIcon />}
              onClick={() => navigate(ROUTES.HOURS_DASHBOARD)}
              sx={{
                color: "white",
                textTransform: "none",
                fontWeight: location.pathname === ROUTES.HOURS_DASHBOARD ? 700 : 400,
                opacity: location.pathname === ROUTES.HOURS_DASHBOARD ? 1 : 0.75,
                borderBottom: location.pathname === ROUTES.HOURS_DASHBOARD ? "2px solid white" : "2px solid transparent",
                borderRadius: 0,
                px: 1.5,
                "&:hover": { opacity: 1, backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              Hours
            </Button>
          </Box>
        )}
        {isAuthenticated && projects.length > 1 && (
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              mr: 2,
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.7)" },
                "&.Mui-focused fieldset": { borderColor: "white" },
              },
              "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "white" },
              "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProject?.name || ""}
              label="Project"
              onChange={handleProjectChange}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.name}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isAuthenticated ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
              <Avatar
                alt="User Image"
                src={user.picture}
                sx={{
                  width: 36,
                  height: 36,
                  border: "2px solid rgba(255,255,255,0.4)",
                }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
                {user.name && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {user.name}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {user.emailaddress || "No Email"}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              {user.role === "Admin" && (
                <>
                  <MenuItem
                    onClick={() => {
                      navigate(ROUTES.SYNC_DASHBOARD);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <SyncIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Sync Dashboard</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate(ROUTES.USER_PROJECT_ACCESSES);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>User Projects Access</ListItemText>
                  </MenuItem>
                </>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <></>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
